/**
 * Resolves the storefront PDP a session was launched from, if anything
 * carried it. Best source first:
 *
 *   1. `?return=` on the invocation — an explicit stamp, when a launcher
 *      ever adds one (none does today).
 *   2. `?client_host_url=` — same reasoning.
 *   3. `document.referrer`, when it is not one of our own hosts — a
 *      direct storefront navigation carries the PDP here (subject to the
 *      site's referrer policy, which may reduce it to an origin).
 *   4. The TOP `location.ancestorOrigins` entry — the SDK's in-storefront
 *      launch navigates its own iframe to the AR app, so the referrer is
 *      our viewer but the top-level document is the PDP. Chrome-only API,
 *      which is exactly this app's runtime.
 *
 * A QR scan carries none of these and resolves null; the report omits the
 * field rather than printing a dash.
 *
 * `deriveEnvironment` (ar-diagnostics) is not reused here: it classifies
 * OUR OWN hostname into an environment, with unknown hosts falling back to
 * `local`. This asks the opposite question — is an ARBITRARY URL ours or a
 * customer's — where an unknown host must mean "customer storefront".
 */

function isFirstPartyHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  if (host === 'av.io' || host.endsWith('.av.io')) return true
  if (host === 'autoviz.io' || host.endsWith('.autoviz.io')) return true
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]'
}

function customerUrl(raw: string | null | undefined): string | null {
  if (raw == null || raw === '') return null
  try {
    const url = new URL(raw)
    return isFirstPartyHost(url.hostname) ? null : raw
  } catch {
    return null
  }
}

export function resolvePdpReferrer(input: {
  returnUrl?: string | null
  clientHostUrl?: string | null
  referrer?: string | null
  ancestorOrigins?: readonly string[]
}): string | null {
  // Every source runs the first-party filter — the PDP field only ever means a
  // customer storefront. The explicit params (returnUrl, clientHostUrl) are
  // PREFERRED, not exempt: a launcher that stamps our own host is still not a
  // page worth naming.
  const returnUrl = customerUrl(input.returnUrl)
  if (returnUrl != null) return returnUrl
  const clientHostUrl = customerUrl(input.clientHostUrl)
  if (clientHostUrl != null) return clientHostUrl
  const referrer = customerUrl(input.referrer)
  if (referrer != null) return referrer
  const ancestors = input.ancestorOrigins ?? []
  // Last entry is the top-level document — the storefront page when the
  // AR app runs inside the SDK's iframe.
  return customerUrl(ancestors[ancestors.length - 1])
}
